import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, OnChanges, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import * as am5plugins_sliceGrouper from '@amcharts/amcharts5/plugins/sliceGrouper';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';

/**
 * Donut Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-donut-graph',
  standalone: false,
  templateUrl: './donut-graph.component.html',
  styleUrl: './donut-graph.component.scss'
})
export class DonutGraphComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private donutGraphReportSubscription: any;
  private isDonutViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService,
    private _spinnerService: SpinnerService) { }
  
  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.donutGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Donut Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isDonutViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isDonutViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('donut-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Donut Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isDonutViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('donut-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Donut Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('donut-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Donut Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('donut-chartdiv');
    if (!chartContainer) {
      console.error('Donut Chart - Chart container not found! Make sure element with id "donut-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Donut Chart - Disposing existing chart...');
      try {
        this._baseChartService.disposeChart(this.root);
      } catch (error) {
        console.warn('Donut Chart - Error disposing existing chart:', error);
      }
      this.root = undefined as any;
    }

    // Additional safety check - ensure no existing am5.Root on the container
    try {
      // Check if there are any existing roots for this container
      const container = document.getElementById('donut-chartdiv');
      if (container && container.children.length > 0) {
        console.log('Donut Chart - Found existing container children, cleaning up...');
        // Clear the container
        container.innerHTML = '';
      }
    } catch (error) {
      console.warn('Donut Chart - Error checking existing container:', error);
    }

    try {
      console.log('Donut Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('donut-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'donut',
        data: data
      });

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      // Create pie chart with inner radius for donut effect
      const pieChart = this.root.container.children.push(am5percent.PieChart.new(this.root, {
        innerRadius: am5.percent(50),
        layout: this.root.verticalLayout
      }));

      // Enhanced chart animation
      this._baseChartService.setupChartAnimation(pieChart, 'elastic');

      // Get background color for strokes
      let bgColor = this.root.interfaceColors.get("background");

      // Use only the first value field for single donut
      const valueField = valueFields[0];
      
      console.log(`Donut Chart - Using field: ${valueField}`);

      // Create single pie series
      let pieSeries = pieChart.series.push(
        am5percent.PieSeries.new(this.root, {
          valueField: valueField,
          categoryField: categoryField,
          alignLabels: false
        })
      );

      // Enhanced series animation
      this._baseChartService.setupSeriesAnimation(pieSeries, 'bounce');

      // Configure series
      pieSeries.ticks.template.setAll({ forceHidden: true });
      pieSeries.labels.template.setAll({ forceHidden: true });
      pieSeries.slices.template.setAll({
        stroke: bgColor,
        strokeWidth: 2,
        tooltipText: `{category}: {valuePercentTotal.formatNumber('0.00')}% ({value})`
      });
      pieSeries.slices.template.states.create("hover", { scale: 0.95 });

      // Set data for the series
      pieSeries.data.setAll(data);

      // Add bullets to show only numbers (values) on slices
      pieSeries.bullets.push((root, series, dataItem) => {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: "{value}",
            fill: am5.color(0x000000),
            centerX: am5.p50,
            centerY: am5.p50,
            populateText: true,
            fontSize: 14,
            fontWeight: "600",
            textAlign: "center"
          })
        });
      });
      
      // Ensure bullets are visible on slices
      pieSeries.slices.template.set("tooltipY", 0);

      // Add legend first
      let legend = pieChart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Configure legend markers
      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      // Add slice grouper plugin - groups slices beyond first 7 into "Others"
      // The plugin automatically handles drill-down and back button
      let grouper = am5plugins_sliceGrouper.SliceGrouper.new(this.root, {
        series: pieSeries,
        clickBehavior: "break",
        threshold: 7,
        groupName: "Others"
      });

      // Function to update legend with current data items
      const updateLegend = () => {
        legend.data.setAll(pieSeries.dataItems);
      };

      // Update legend after grouper processes the data
      // Use setTimeout to ensure grouper has processed the data items
      setTimeout(() => {
        updateLegend();
      }, 100);

      // Update legend when slices are clicked (for drill-down/up)
      pieSeries.slices.template.events.on("click", () => {
        setTimeout(() => {
          updateLegend();
        }, 50);
      });

      // Enhanced legend animation
      this._baseChartService.setupLegendAnimation(legend);

      // Trigger enhanced chart animation
      setTimeout(() => {
        pieSeries.appear(1000, 100);
      }, 100);

      console.log('Donut Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating donut chart:', error);
      this.isCreatingChart = false;
      
      // Clean up on error
      if (this.root) {
        try {
          this._baseChartService.disposeChart(this.root);
        } catch (disposeError) {
          console.warn('Donut Chart - Error disposing chart on error:', disposeError);
        }
        this.root = undefined as any;
      }
    }
  }
  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Donut Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('donut-chartdiv');
    if (!chartContainer) {
      console.log('Donut Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Update the stored data
    this.data = data;

    // Dispose existing chart first
    if (this.root) {
      console.log('Donut Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    // Ensure chart is properly disposed
    if (this.root) {
      console.log('Donut Chart - Disposing chart in ngOnDestroy...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Unsubscribe from the subscription
    if (this.donutGraphReportSubscription) {
      this.donutGraphReportSubscription.unsubscribe();
    }
    
    // Reset flags
    this.isCreatingChart = false;
    this.isDonutViewInitialized = false;
    
    // Hide spinner if it's still showing
    this._spinnerService.hide();
  }
}
